import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { useAuthPermissions, useAuthStore, useAuthEmail } from '@/stores/auth.store';
import { useCompanySettings } from '@/hooks/settings/useCompanySettings';
import { useMyProfile } from '@/hooks/useMyProfile';
import { greetingFirstName } from '@/lib/users/greetingName';
import { useNotificationUnreadQuery } from '@/hooks/notifications/useStaffNotifications';
import { useNotificationUnreadCount } from '@/stores/notification.store';
import { useUiActions } from '@/stores/ui.store';
import { useLogout } from '@/hooks/useAuth';
import { Bell, LogOut, Search, Settings } from 'lucide-react';
import { NotificationSheet } from '@/components/layout/GlobalNotificationBell';
import { DialerTrigger } from '@/components/voip/Dialer';
import { ConferenceTrigger } from '@/components/conference/ConferenceTrigger';
import { GroupMessagingTrigger } from '@/components/group-messaging/GroupMessagingTrigger';
import { LiveCallsTrigger } from '@/components/calls/live/global/LiveCallsTrigger';
import { Separator } from '@/components/atoms/separator';
import { ThemeToggleMenuItem } from '@/components/layout/ThemeToggle';
import { DoNotDisturbMenuItem } from '@/components/voip/DoNotDisturbMenuItem';
import { useDoNotDisturb } from '@/hooks/voip/useDoNotDisturb';
import { useDashboardHeaderSlots } from '@/contexts/dashboard-header';

function greetingForHour(h: number): string {
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function Header() {
  const dashboardHeaderSlots = useDashboardHeaderSlots();
  const userId = useAuthStore((s) => s.userId);
  const email = useAuthEmail();
  const permissions = useAuthPermissions();
  const company = useCompanySettings();
  const notificationUnread = useNotificationUnreadCount();
  const canViewNotifications = permissions.includes('notifications:view');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  useNotificationUnreadQuery({ enabled: canViewNotifications });
  const logout = useLogout();
  const { openModal } = useUiActions();
  const doNotDisturb = useDoNotDisturb();

  const { data: myProfile } = useMyProfile();
  const firstName =
    greetingFirstName({
      firstName: myProfile?.firstName,
      displayName: myProfile?.displayName,
      email,
    }) ?? 'there';
  const greeting = greetingForHour(new Date().getHours());
  const companyName = company.data?.name;
  const companyLogo = company.data?.logoUrl;
  const companyPhone = company.data?.phone;
  const companyInitial = (companyName?.trim().charAt(0) || 'N').toUpperCase();

  return (
    <header className="flex h-13 shrink-0 items-center gap-3 border-b bg-background px-4">
      {dashboardHeaderSlots.context?.node ? (
        <div className="min-w-0 flex-1">{dashboardHeaderSlots.context.node}</div>
      ) : (
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">
            {greeting}, {firstName}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {dashboardHeaderSlots.actions?.node ? (
          <>
            <div className="mr-1 flex items-center gap-2">{dashboardHeaderSlots.actions.node}</div>
            <Separator orientation="vertical" className="mx-1 h-6" />
          </>
        ) : null}
        {/* Search / Command Palette trigger — icon only at every width. */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => openModal('commandPalette')}
          aria-label="Search across records and pages"
          aria-keyshortcuts="Meta+K Control+K"
        >
          <Search className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="mx-1 h-6" />

        {/* Call (first) */}
        <DialerTrigger />

        {/* Conference — reachable from anywhere in the dashboard, not just the
            calls page. Opens the overlay mounted at the dashboard root. */}
        <ConferenceTrigger />

        {/* Native group MMS for technician coordination. */}
        <GroupMessagingTrigger />

        {/* Calls live on the account right now, with a count. Appears only when
            there are some — and is the way back to them once the floating bubble
            has been dismissed. */}
        <LiveCallsTrigger />

        {/* Notifications (second) */}
        {canViewNotifications ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="relative h-9 w-9"
              aria-label={
                notificationUnread > 0
                  ? `Notifications, ${notificationUnread} unread`
                  : 'Notifications'
              }
              aria-controls="global-notification-panel"
              aria-expanded={notificationsOpen}
              onClick={() => setNotificationsOpen(true)}
            >
              <Bell className="h-[18px] w-[18px]" />
              {notificationUnread > 0 ? (
                <span className="absolute right-1 top-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-destructive px-1 text-[8px] font-semibold leading-none text-white">
                  {notificationUnread > 99 ? '99+' : notificationUnread}
                </span>
              ) : null}
            </Button>
            <NotificationSheet open={notificationsOpen} onOpenChange={setNotificationsOpen} />
          </>
        ) : null}

        <Separator orientation="vertical" className="mx-1 h-6" />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-8 w-8 rounded-full"
              aria-label={
                doNotDisturb.isOn
                  ? `User menu for ${userId}. Do Not Disturb is on: calls are silenced on this computer ${doNotDisturb.endsLabel ?? 'until you turn ringing back on'}.`
                  : `User menu for ${userId}`
              }
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
                {companyInitial}
              </span>
              {/* Kept visible outside the menu, so DND left on by accident is
                  noticed before a day of missed calls. */}
              {doNotDisturb.isOn ? (
                <span
                  aria-hidden
                  className="absolute bottom-0 right-0 flex h-3 w-3 items-center justify-center rounded-full border-2 border-background bg-destructive"
                >
                  <span className="block h-[2px] w-1.5 rounded-full bg-white" />
                </span>
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="flex items-center gap-2.5 px-2 py-2">
              <Avatar className="h-8 w-8 shrink-0 rounded-md">
                {companyLogo ? (
                  <AvatarImage
                    src={companyLogo}
                    alt={companyName ?? 'Company'}
                    className="rounded-md"
                  />
                ) : null}
                <AvatarFallback className="rounded-md bg-primary text-[10px] font-semibold text-primary-foreground">
                  {(companyName ?? 'N').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 leading-tight">
                <p className="truncate text-sm font-semibold text-foreground">
                  {companyName ?? 'Your workspace'}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {companyPhone ?? email ?? 'No number set'}
                </p>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/dashboard/settings" className="flex items-center gap-2 cursor-pointer">
                <Settings className="h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            {doNotDisturb.isAvailable ? (
              <DoNotDisturbMenuItem
                devices={doNotDisturb.devices}
                thisBrowser={doNotDisturb.thisBrowser}
                timeZone={doNotDisturb.timeZone}
                disabled={doNotDisturb.isLoading || doNotDisturb.isSaving}
                onTurnOn={doNotDisturb.turnOn}
                onTurnOff={doNotDisturb.turnOff}
              />
            ) : null}
            <ThemeToggleMenuItem />
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="flex items-center gap-2 text-destructive focus:text-destructive cursor-pointer"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
